import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { TrendingUp, TrendingDown, Search, ArrowRight, Zap, Shield, Globe, BarChart3, Clock, Users, Menu } from 'lucide-react';
import { fetchApi, formatPrice, formatPercent, getChangeColor } from '../lib/utils';
import Marquee from 'react-fast-marquee';

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, googleLogin } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [topStocks, setTopStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const loadStocks = async () => {
      try {
        // Initialize stocks if needed
        await fetchApi('/init-stocks', { method: 'POST' }).catch(() => {});
        
        const data = await fetchApi('/stocks?limit=20');
        setTopStocks(data.stocks || []);
      } catch (error) {
        console.error('Failed to load stocks:', error);
      } finally {
        setLoading(false);
      }
    };
    loadStocks();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/stocks?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const features = [
    {
      icon: Zap,
      title: 'AI-Powered Predictions',
      description: 'Advanced NLP analyzes thousands of news articles to predict stock movements with confidence scores.'
    },
    {
      icon: Globe,
      title: 'Global Coverage',
      description: 'Track stocks, ETFs, and assets from all major markets worldwide.'
    },
    {
      icon: Clock,
      title: 'Real-Time Updates',
      description: 'Predictions update continuously as new information becomes available.'
    },
    {
      icon: BarChart3,
      title: 'Multiple Timeframes',
      description: 'View predictions for hours, days, weeks, months, or years ahead.'
    },
    {
      icon: Shield,
      title: 'Transparent Reasoning',
      description: 'Every prediction includes detailed reasoning and source articles.'
    },
    {
      icon: Users,
      title: 'Portfolio Tracking',
      description: 'Record purchases and compare AI predictions against real performance.'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-sm bg-primary flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-lg sm:text-xl">MarketMind</span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/stocks" className="text-muted-foreground hover:text-foreground transition-colors">
              Stocks
            </Link>
            <Link to="/forum" className="text-muted-foreground hover:text-foreground transition-colors">
              Forum
            </Link>
            <Link to="/search" className="text-muted-foreground hover:text-foreground transition-colors">
              Advanced Search
            </Link>
            {isAuthenticated ? (
              <Button data-testid="dashboard-nav-btn" onClick={() => navigate('/dashboard')} className="glow-primary">
                Dashboard
              </Button>
            ) : (
              <Button data-testid="login-nav-btn" onClick={() => navigate('/auth')} className="glow-primary">
                Sign In
              </Button>
            )}
          </div>

          {/* Mobile Navigation */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="landing-mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] bg-card">
              <SheetHeader>
                <SheetTitle className="text-left">Menu</SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-4 mt-6">
                <Link 
                  to="/stocks" 
                  className="text-foreground hover:text-primary transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Stocks
                </Link>
                <Link 
                  to="/forum" 
                  className="text-foreground hover:text-primary transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Forum
                </Link>
                <Link 
                  to="/search" 
                  className="text-foreground hover:text-primary transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Advanced Search
                </Link>
                {isAuthenticated ? (
                  <Button 
                    data-testid="mobile-dashboard-btn" 
                    onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                    className="w-full glow-primary"
                  >
                    Dashboard
                  </Button>
                ) : (
                  <Button 
                    data-testid="mobile-signin-btn" 
                    onClick={() => { navigate('/auth'); setMobileMenuOpen(false); }}
                    className="w-full glow-primary"
                  >
                    Sign In
                  </Button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 sm:pt-32 pb-12 sm:pb-20 px-4 sm:px-6 hero-gradient overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7 space-y-6 sm:space-y-8">
              <div className="space-y-3 sm:space-y-4">
                <p className="font-mono text-xs uppercase tracking-widest text-primary">
                  AI-Powered Stock Intelligence
                </p>
                <h1 className="font-heading font-bold text-3xl sm:text-5xl md:text-7xl tracking-tight leading-tight sm:leading-none">
                  Predict Market Moves Before They Happen
                </h1>
                <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-xl">
                  Harness the power of AI to analyze global news and generate accurate stock predictions with confidence levels and detailed reasoning.
                </p>
              </div>

              {/* Search Bar */}
              <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 max-w-md">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    data-testid="hero-search-input"
                    placeholder="Search any stock (e.g., AAPL)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-muted/50 border-input focus:border-primary h-12"
                  />
                </div>
                <Button data-testid="hero-search-btn" type="submit" className="h-12 px-6 glow-primary w-full sm:w-auto">
                  Search
                </Button>
              </form>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                <Button data-testid="explore-stocks-btn" onClick={() => navigate('/stocks')} variant="outline" size="lg" className="w-full sm:w-auto">
                  Explore Stocks <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Hero Image - Hidden on small mobile, shown on larger screens */}
            <div className="lg:col-span-5 hidden sm:block">
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1762279389083-abf71f22d338?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                  alt="AI Stock Analysis"
                  className="rounded-sm border border-border/50 shadow-2xl w-full"
                />
                <div className="absolute -bottom-4 sm:-bottom-6 -left-2 sm:-left-6 p-3 sm:p-4 glass rounded-sm">
                  <p className="text-xs text-muted-foreground mb-1">NVDA Prediction</p>
                  <p className="font-mono text-base sm:text-lg text-up">+8.5%</p>
                  <p className="text-xs text-muted-foreground">Confidence: 78%</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stock Ticker */}
      {topStocks.length > 0 && (
        <section className="py-4 border-y border-border/50 bg-card">
          <Marquee speed={40} gradient={false} pauseOnHover>
            {topStocks.map((stock, idx) => (
              <Link
                key={idx}
                to={`/stocks/${stock.ticker}`}
                className="flex items-center gap-3 px-6 hover:bg-accent/50 py-2 transition-colors"
              >
                <span className="font-mono font-medium">{stock.ticker}</span>
                <span className="text-muted-foreground">{formatPrice(stock.current_price)}</span>
                <span className={`font-mono text-sm ${getChangeColor(stock.price_change_pct_24h)}`}>
                  {formatPercent(stock.price_change_pct_24h)}
                </span>
              </Link>
            ))}
          </Marquee>
        </section>
      )}

      {/* Features Grid */}
      <section className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-3 sm:mb-4">
              Why MarketMind
            </p>
            <h2 className="font-heading font-semibold text-2xl sm:text-3xl md:text-5xl tracking-tight">
              Intelligence That Moves Markets
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {features.map((feature, idx) => (
              <Card key={idx} className="bg-card border-border/50 hover:border-primary/50 transition-all duration-300 card-hover">
                <CardHeader className="pb-2 sm:pb-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-sm bg-primary/10 flex items-center justify-center mb-3 sm:mb-4">
                    <feature.icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                  </div>
                  <CardTitle className="font-heading text-lg sm:text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm text-muted-foreground">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 hero-gradient">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-semibold text-2xl sm:text-3xl md:text-5xl tracking-tight mb-4 sm:mb-6">
            Start Making Smarter Trades Today
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground mb-6 sm:mb-8 max-w-2xl mx-auto">
            Get AI-powered stock predictions and stay ahead of the market.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Button 
              data-testid="cta-google-btn"
              onClick={googleLogin} 
              size="lg" 
              className="glow-primary animate-pulse-glow w-full sm:w-auto"
            >
              Continue with Google
            </Button>
            <Button 
              data-testid="cta-browse-btn"
              onClick={() => navigate('/stocks')} 
              variant="outline" 
              size="lg"
              className="w-full sm:w-auto"
            >
              Browse Without Account
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-12 px-4 sm:px-6 border-t border-border/50">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-sm bg-primary flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold">MarketMind</span>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            <div className="flex gap-6 text-sm">
              <Link to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</Link>
              <Link to="/about" className="text-muted-foreground hover:text-foreground transition-colors">About</Link>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground text-center sm:text-right">
              © 2025 MarketMind. AI predictions are not financial advice.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
